import React, { useEffect, useRef, useState } from "react";
import PageLoader from "../../../shared/PageLoader";
import { useAppDispatch, useAppSelector } from "../../../../redux/hook";
import { IoMdClose } from "react-icons/io";
import { useExportEmployeesAttendance } from "../../../../backend/exports";
import { downloadFileContent } from "../../../../utils/common";
import { ERROR_MESSAGES } from "../../../../utils/constants";
import { toast } from "react-toastify";
import {
  bulkDeleteEmployeeAttendance,
  getEmployeesAttendance,
} from "../../../../redux/features/performerClient.slice";
import {
  resetBulkDeleteHolidaysDetails,
  resetUploadHolidayDetails,
} from "../../../../redux/features/holidayList.slice";
import EmployeeUploadFilters from "../shared/EmployeeUploadFilters";
import EmployeeUploadSearch from "../../../common/EmployeeUploadSearch";
import AdditionalFiltersInputUploads from "../shared/AdditionalFiltersInputUploads";
import {
  useEmployeeUploadFilters,
  useEmployeeUploadSearchConfig,
  useEmployeeUploadData,
  usePerformerClientCompanyData,
} from "../../../../hooks/useEmployeeUploads";
import {
  getEmployeeUploadCompanyNameById,
  getEmployeeUploadAssocCompanyNameById,
  getEmployeeUploadLocationById,
  buildEmployeeUploadQueryPayload,
} from "../../../../utils/employeeUploads.utils";
import { UploadButton } from "../shared";
import PerformerTable from "../shared/PerformerTable";
import "../shared/performer-client.css";
import Icon from "../../../common/Icon";


const EmployeeAttendanceUpload = () => {
  const dispatch = useAppDispatch();

  // Redux selectors
  const employeesAttendanceDetails = useAppSelector(
    (state) => state.performerClient.employeesAttendanceDetails,
  );
  const bulkDeleteEmployeesAttendanceDetails = useAppSelector(
    (state) => state.performerClient.bulkDeleteEmployeeAttendance,
  );
  const uploadHolidayDetails = useAppSelector(
    (state) => state.holidayList.uploadHolidayDetails,
  );

  // Use custom hooks
  const { filters, updateFilter, updateMultipleFilters } =
    useEmployeeUploadFilters();
  const { searchConfig, updateSearchConfig } = useEmployeeUploadSearchConfig({
    activeSort: "created_at",
    sortType: "desc",
  });
  const { fetchData, fetchWithSearch } = useEmployeeUploadData({
    monthFieldName: "wage_month_eq",
    searchFieldName: "employee_code_cont",
  });
  const {
    companies,
    associateCompanies,
    locations,
    fetchCompanies,
    fetchAssociateCompanies,
    fetchLocations,
  } = usePerformerClientCompanyData();

  // Local state for UI components
  const [companyName, setCompanyName] = useState("");
  const [associate_company_name, setAssociateCompanyName] = useState("");
  const [locationName, setLocationName] = useState("");
  const [companyLocationMappingId, setCompanyLocationMappingId] = useState("");
  const [showAdditionalFiltersModal, setShowAdditionalFiltersModal] =
    useState(false);
  const [selectedAdditionalFilters, setSelectedAdditionalFilters] =
    useState<any>({});

  const [selectedAttendance, setSelectedAttendance] = useState<any>([]);
  const [openBulkDeleteModal, setOpenBulkDeleteModal] = useState(false);
  const [uploadError, setUploadError] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);

  const employeesAttendance = employeesAttendanceDetails.data.items || [];
  const employeesAttendanceCount =
    employeesAttendanceDetails.data.meta.total_count;

  const { exportRecords, exporting } = useExportEmployeesAttendance(
    (response: any) => {
      let filename =
        associate_company_name +
        "_" +
        locationName +
        "_" +
        "Employee-Attendance" +
        "_" +
        filters.year +
        "_" +
        filters.month +
        ".xlsx";
      toast.success('Export will be sent to your registered email.');
    },
    () => {
      toast.error(ERROR_MESSAGES.DEFAULT);
    },
  );

  const loading =
    exporting ||
    uploadHolidayDetails.status === "loading" ||
    bulkDeleteEmployeesAttendanceDetails.status === "loading" ||
    employeesAttendanceDetails.status === "loading";

  // Data fetching effects
  useEffect(() => {
    fetchCompanies();
    fetchData(getEmployeesAttendance, filters, searchConfig);
  }, []);

  // Fetch data when filters change (including additional filters and year/month)
  const isInitialRender = useRef(true);
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      return;
    }
    updateSearchConfig({ page: 0 });
    fetchData(getEmployeesAttendance, filters, { ...searchConfig, page: 0 });
  }, [
    filters.establishmentType,
    filters.company,
    filters.associateCompany,
    filters.location,
    filters.year,
    filters.month,
    filters.employer,
    filters.employerLocation,
    filters.subContractor,
    filters.subContractorLocation,
  ]);

  useEffect(() => {
    if (filters.company) {
      fetchAssociateCompanies(filters.company);
      const selectedCompanyName = getEmployeeUploadCompanyNameById(
        filters.company,
        companies,
      );
      setCompanyName(selectedCompanyName);
    }
  }, [filters.company, companies]);

  useEffect(() => {
    if (filters.associateCompany) {
      fetchLocations(filters.associateCompany);
      const selectedCompanyName = getEmployeeUploadAssocCompanyNameById(
        filters.associateCompany,
        associateCompanies,
      );
      setAssociateCompanyName(selectedCompanyName);
    }
  }, [filters.associateCompany, associateCompanies]);

  useEffect(() => {
    if (filters.location) {
      const selectedLocationId = filters.location.split("^")[0];
      const selectedLocationName = getEmployeeUploadLocationById(
        selectedLocationId,
        locations,
      );
      setLocationName(selectedLocationName);
      const tempLocation =
        locations.find((loc: any) => loc.location_id === selectedLocationId) ||
        {};
      setCompanyLocationMappingId(tempLocation.company_location_mapping_id || "");
    }
  }, [filters.location, locations]);

  // Clear additional filters when parent filters change
  useEffect(() => {
    setSelectedAdditionalFilters({});
  }, [
    filters.establishmentType,
    filters.company,
    filters.associateCompany,
    filters.location,
  ]);

  // Handler functions
  const handleSearch = () => {
    updateSearchConfig({ page: 0 });
    fetchData(getEmployeesAttendance, filters, { ...searchConfig, page: 0 });
  };

  const handleClearSearch = () => {
    updateSearchConfig({ searchInput: "" });
    fetchData(getEmployeesAttendance, filters, { ...searchConfig, searchInput: "" });
  };

  const handleSort = (sortField: string) => {
    const newSortDirection =
      searchConfig.activeSort === sortField && searchConfig.sortType === "asc"
        ? "desc"
        : "asc";
    updateSearchConfig({
      activeSort: sortField,
      sortType: newSortDirection,
    });
    fetchData(
      getEmployeesAttendance,
      filters,
      searchConfig,
      {},
      sortField,
      newSortDirection,
    );
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    updateSearchConfig({ page: newPage });
    fetchData(getEmployeesAttendance, filters, {
      ...searchConfig,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    updateSearchConfig({
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
    fetchData(getEmployeesAttendance, filters, {
      ...searchConfig,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const handleAdditionalFiltersApply = (filterData: any) => {
    updateMultipleFilters({
      employer: filterData.payload.employer_id_eq || "",
      employerLocation: filterData.payload.employer_location_id_eq || "",
      subContractor: filterData.payload.sub_contractor_id_eq || "",
      subContractorLocation:
        filterData.payload.sub_contractor_location_id_eq || "",
    });
    setSelectedAdditionalFilters(filterData.selectedFilters || {});
    setShowAdditionalFiltersModal(false);
  };

  const onClickConfirmBulkDelete = () => {
    dispatch(bulkDeleteEmployeeAttendance(selectedAttendance));
  };

  useEffect(() => {
    if (bulkDeleteEmployeesAttendanceDetails.status === "succeeded") {
      toast.success(`Attendance deleted successfully.`);
      setSelectedAttendance([]);
      dispatch(resetBulkDeleteHolidaysDetails());
      setOpenBulkDeleteModal(false);
      fetchData(getEmployeesAttendance, filters, searchConfig);
    } else if (bulkDeleteEmployeesAttendanceDetails.status === "failed") {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [bulkDeleteEmployeesAttendanceDetails.status]);

  useEffect(() => {
    if (uploadHolidayDetails.status === "succeeded") {
      if (uploadHolidayDetails.data.size === 0) {
        toast.success(`Holiday List Uploaded successfully.`);
        dispatch(resetUploadHolidayDetails());
        setOpenUploadModal(false);
        setUploadError(false);
        fetchData(getEmployeesAttendance, filters, searchConfig);
      } else {
        setUploadError(true);
      }
    } else if (uploadHolidayDetails.status === "failed") {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [uploadHolidayDetails]);

  useEffect(() => {
    if (employeesAttendanceDetails.status === "succeeded") {
    } else if (employeesAttendanceDetails.status === "failed") {
      toast.error(ERROR_MESSAGES.DEFAULT);
    }
  }, [employeesAttendanceDetails.status]);

  const onClickExport = () => {
    const { per, page, ...payload } = buildEmployeeUploadQueryPayload(
      filters,
      searchConfig,
      {
        monthFieldName: "wage_month_eq",
        searchFieldName: "employee_code_cont",
      },
      {},
      "employee_code",
      "asc",
    );
    exportRecords(payload);
  };


  return (
    <>
    <div className="performerclient-wrapper h-100">
      <div className="performer-client-page h-100 py-3">
      {openBulkDeleteModal && (
        <div
          style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1300, display: "flex", justifyContent: "center" }}
          onClick={() => setOpenBulkDeleteModal(false)}
        >
          <div
            style={{ position: "relative", width: 600, height: "fit-content", backgroundColor: "#ffffff", boxShadow: "0px 11px 15px rgba(0,0,0,0.2)" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ backgroundColor: "#E2E3F8", padding: "10px 20px", borderRadius: "6px", boxShadow: "0px 6px 10px #CDD2D9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ font: "normal normal normal 32px/40px Calibri" }}>Delete Employees</span>
              <button onClick={() => setOpenBulkDeleteModal(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: "8px" }}>
                <IoMdClose />
              </button>
            </div>
            <div style={{ padding: "20px", backgroundColor: "#ffffff" }}>
              <div>
                <h5>There are {selectedAttendance.length} record(s) selected for deleting.</h5>
                <p style={{ marginTop: "16px" }}>Are you sure you want to delete all of them ?</p>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "16px" }}>
                <button onClick={() => setOpenBulkDeleteModal(false)} style={{ border: "1px solid red", color: "red", backgroundColor: "transparent", padding: "6px 16px", borderRadius: "4px", cursor: "pointer" }}>No</button>
                <button onClick={onClickConfirmBulkDelete} style={{ backgroundColor: "#1976d2", color: "#ffffff", border: "none", padding: "6px 16px", borderRadius: "4px", cursor: "pointer" }}>Yes</button>
              </div>
            </div>
          </div>
        </div>
      )}

        <div style={{ height: "100%" }}>
          <div className="pageHeading mb-0" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h4 className="mb-0">Employee Attendance</h4>
            <EmployeeUploadSearch
              value={searchConfig.searchInput}
              placeholder="Search (Employee Code)"
              onChange={(e) => updateSearchConfig({ searchInput: e.target.value })}
              onSearch={handleSearch}
              onClear={handleClearSearch}
            />
          </div>
          <div className="filters" style={{ padding: "16px 20px" }}>
            <div style={{ backgroundColor: "#ffffff", padding: "16px 16px", borderRadius: "8px" }}>
              <EmployeeUploadFilters
                filters={filters}
                companies={companies}
                associateCompanies={associateCompanies}
                locations={locations}
                onUpdateFilter={updateFilter}
                showYearMonth={true}
                showAdditionalFilters={true}
                onAdditionalFiltersClick={() => {
                  if (
                    filters.company &&
                    filters.associateCompany &&
                    filters.location
                  ) {
                    setShowAdditionalFiltersModal(true);
                  } else {
                    toast.error(
                      "Please select Company, Associate Company and Location to apply additional filters",
                    );
                  }
                }}
                actions={
                  <>
                    <div style={{ cursor: (!filters.company || !filters.associateCompany) ? "not-allowed" : "pointer" }}>
                      <button
                        onClick={onClickExport}
                        disabled={!filters.company || !filters.associateCompany}
                        className="download-btn"
                        title='Export Data'
                      >
                        <Icon name="downloadIcon" />
                      </button>
                    </div>
                    <UploadButton
                      configType="Employee Attendance"
                      filters={{
                        company: filters.company,
                        associateCompany: filters.associateCompany,
                        establishmentType: filters.establishmentType,
                        location: filters.location ? filters.location.split("^")[0] : "",
                      }}
                      selectedAdditionalFilters={selectedAdditionalFilters}
                      onUploadSuccess={() =>
                        fetchData(getEmployeesAttendance, filters, searchConfig)
                      }
                    />
                  </>
                }
              />
            </div>
          </div>

          {/* Additional Filters Modal */}
          {showAdditionalFiltersModal && (
            <AdditionalFiltersInputUploads
              data={{
                establishmentType: filters.establishmentType,
                company: filters.company,
                associate_company: filters.associateCompany,
                location: companyLocationMappingId,
              }}
              selectedFilters={selectedAdditionalFilters}
              onSubmit={handleAdditionalFiltersApply}
              onCancel={() => setShowAdditionalFiltersModal(false)}
            />
          )}

          <div style={{ padding: "0 20px" }}>
            <PerformerTable
              columns={[
                { label: "Employee Code", sortKey: "employee_code", render: (row) => row.employee_code },
                ...(filters.establishmentType === "CLRA" ? [{ label: "Company Employer Sub-Contractor Code", render: (row: any) => (row.company_employer_sub_contractor || {}).company_employer_sub_contractor_code }] : []),
                { label: "Present Days", sortKey: "present_days", render: (row) => row.present_days },
                { label: "Wage Month", sortKey: "wage_month", render: (row) => row.wage_month },
                { label: "Office In TIME", render: (row) => row.office_in_time },
                { label: "Office Out Time", render: (row) => row.office_out_time },
                { label: "Interval In Time", render: (row) => row.interval_in_time },
                { label: "Interval Out Time", render: (row) => row.interval_out_time },
                { label: "Day 1", render: (row) => row.day1 },
                { label: "Day 2", render: (row) => row.day2 },
                { label: "Day 3", render: (row) => row.day3 },
                { label: "Day 4", render: (row) => row.day4 },
                { label: "Day 5", render: (row) => row.day5 },
                { label: "Day 6", render: (row) => row.day6 },
                { label: "Day 7", render: (row) => row.day7 },
                { label: "Day 8", render: (row) => row.day8 },
                { label: "Day 9", render: (row) => row.day9 },
                { label: "Day 10", render: (row) => row.day10 },
                { label: "Day 11", render: (row) => row.day11 },
                { label: "Day 12", render: (row) => row.day12 },
                { label: "Day 13", render: (row) => row.day13 },
                { label: "Day 14", render: (row) => row.day14 },
                { label: "Day 15", render: (row) => row.day15 },
                { label: "Day 16", render: (row) => row.day16 },
                { label: "Day 17", render: (row) => row.day17 },
                { label: "Day 18", render: (row) => row.day18 },
                { label: "Day 19", render: (row) => row.day19 },
                { label: "Day 20", render: (row) => row.day20 },
                { label: "Day 21", render: (row) => row.day21 },
                { label: "Day 22", render: (row) => row.day22 },
                { label: "Day 23", render: (row) => row.day23 },
                { label: "Day 24", render: (row) => row.day24 },
                { label: "Day 25", render: (row) => row.day25 },
                { label: "Day 26", render: (row) => row.day26 },
                { label: "Day 27", render: (row) => row.day27 },
                { label: "Day 28", render: (row) => row.day28 },
                { label: "Day 29", render: (row) => row.day29 },
                { label: "Day 30", render: (row) => row.day30 },
                { label: "Day 31", render: (row) => row.day31 },
              ]}
              data={employeesAttendance}
              count={employeesAttendanceCount}
              page={searchConfig.page}
              rowsPerPage={searchConfig.rowsPerPage}
              activeSort={searchConfig.activeSort}
              sortType={searchConfig.sortType}
              onSort={handleSort}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </div>
        </div>
      </div>
    </div>
    {loading && <PageLoader>Loading...</PageLoader>}
    </>
  );
};

export default EmployeeAttendanceUpload;
